import products from '../../../../services/products';
import addItem from '../../../tac/tacReducer/addItem';
import geometry from '../../../../scene/util/geometry';
import { flatten } from '../../../../util/array';
import {
  getMatchingConnections,
  fitToPoint,
} from '../../../../services/products/models';
import removeItem from '../../tacReducer/removeItem';
import { ITEMS } from '../../../../constants';

function bracketsAdded(tac, rebracketedUprights) {
  return rebracketedUprights
    .map(upright => ({
      ...upright,
      items: upright.items.filter(item => !item.itemid),
    }))
    .filter(upright => upright.items.length);
}

function bracketsRemoved(updTac, rebracketedUprights) {
  const oldUprights = updTac.items.filter(item =>
    products.isType(item.id, ITEMS.UPRIGHT)
  );
  const remainingBrackets = flatten(
    rebracketedUprights.map(upright => upright.items)
  ).filter(item => products.isType(item.id, 'bracket'));
  return oldUprights
    .map(upright => ({
      ...upright,
      items: upright.items.filter(
        item =>
          !remainingBrackets.find(bracket => bracket.itemid === item.itemid)
      ),
    }))
    .filter(upright => upright.items.length);
}

export function fitBracket(upright, gItem, bracket) {
  const localY =
    gItem.y -
    upright.y +
    (!products.isType(gItem, ITEMS.TABLE) ? gItem.height : 0);
  const conns = getMatchingConnections(bracket, { items: [upright] })[0]
    .connections;
  const fittedBrackets = conns.map(conn => fitToPoint(bracket, conn, 'size'));

  const bestBracket = fittedBrackets
    .sort((a, b) => (a.y > b.y ? -1 : a.y < b.y ? 1 : 0))
    .find(bracket => bracket.y < localY);

  if (!bestBracket) {
    // nothing remotely usable
    return null;
  }

  if (Math.abs(bestBracket.y - localY) > bestBracket.height) {
    // not even the best bracket can be used!
    return null;
  }
  return bestBracket;
}

// determine if a colliding item should have a bracket on upright
function properBracketPos(item, upright) {
  const closeEnough = 20; //mm
  const uprightx = upright.x + upright.width / 2;
  const leftDiff = Math.abs(item.x - uprightx);
  const rightDiff = Math.abs(item.x + item.width - uprightx);
  if (rightDiff > closeEnough && leftDiff > closeEnough) {
    // this upright is not where a bracket should be.
    return false;
  }
  return true;
}

/*
returns a list of all uprights that changed compared to the ones in tac
 */
function addAllBrackets(tac) {
  const gAllBracketItems = flatten(
    tac.items.map(parent =>
      parent.items.map(child =>
        Object.assign({}, child, {
          x: child.x + parent.x,
          y: child.y + parent.y,
          z: child.z + parent.z,
        })
      )
    )
  ).filter(item => products.getFittingBracket(item));
  const initialUprights = tac.items.filter(item =>
    products.isType(item.id, ITEMS.UPRIGHT)
  );
  const newUprights = initialUprights.map(upright =>
    Object.assign({}, upright, { items: [] })
  );

  newUprights.forEach(upright => {
    upright.items = gAllBracketItems
      .filter(
        item =>
          geometry.collides(item, upright, geometry.uniformPadding(1)) &&
          properBracketPos(item, upright)
      )
      .filter(coll => coll.itemid !== upright.itemid)
      .map(item => {
        const bracket =
          products.getFittingBracket(item, undefined, item.filter?.color) ||
          products.getFittingBracket(item, undefined, upright.filter?.color) ||
          products.getFittingBracket(item);
        if (bracket) {
          const fitted = fitBracket(upright, item, bracket);
          // linnmon's brackets are below it, so might not fit even though we collide
          return fitted
            ? {
                ...bracket,
                x: (upright.width - bracket.width) / 2,
                y: fitted.y,
                z: fitted.z,
              }
            : null;
        }
        return null;
      })
      .filter(Boolean);
  });
  // ignore uprights where the recalculated brackets were already present
  const updatedUprights = newUprights;

  // remove any recreated brackets
  updatedUprights.forEach(upright => {
    const oldUpright = initialUprights.find(
      iupright => iupright.itemid === upright.itemid
    );
    upright.items = upright.items.map(
      bracket =>
        oldUpright.items.find(
          item =>
            item.y === bracket.y && item.filter?.color === bracket.filter?.color
        ) || bracket
    );
    let uprightItemsWithoutDuplicates = [];
    upright.items.forEach(bracket => {
      const alreadyAddedBracketAtSamePosition =
        uprightItemsWithoutDuplicates.find(
          alreadyAddedBracket => alreadyAddedBracket.y === bracket.y
        );
      if (alreadyAddedBracketAtSamePosition) {
        if (
          alreadyAddedBracketAtSamePosition.filter?.color ===
            bracket.filter?.color ||
          alreadyAddedBracketAtSamePosition.filter?.color ===
            upright.filter?.color
        )
          return;
        else {
          uprightItemsWithoutDuplicates = uprightItemsWithoutDuplicates.filter(
            alreadyAddedBracket =>
              alreadyAddedBracket !== alreadyAddedBracketAtSamePosition
          );
        }
      }
      uprightItemsWithoutDuplicates.push(bracket);
    });
    upright.items = uprightItemsWithoutDuplicates;
  });

  return updatedUprights;
}

export function causedBracketChanges(tac) {
  if (!tac) {
    throw new Error('causedBracketChanges requires all params');
  }

  const out = {
    added: [],
    removed: [],
  };

  const rebracketedUprights = addAllBrackets(tac);

  out.added = bracketsAdded(tac, rebracketedUprights);
  out.removed = bracketsRemoved(tac, rebracketedUprights);
  return out;
}

/*
  these extra functions are just to avoid
  rewriting all the tacs I had in my test spec, since previously
  I asked about what would happen, rather than the current case,
  where I ask what did happen.

*/

// adds an item to tac before asking about brackets
function add(tac, item, parent) {
  const updTac = addItem(tac, item, parent);
  const rebracketedUprights = addAllBrackets(updTac);
  return {
    add: bracketsAdded(updTac, rebracketedUprights),
    remove: [],
  };
}

// removes an item from tac before asking about brackets
function remove(tac, item, parent) {
  const updTac = removeItem(tac, item, parent);
  const rebracketedUprights = addAllBrackets(updTac);
  return {
    add: [],
    remove: bracketsRemoved(updTac, rebracketedUprights),
  };
}

export const test = {
  addAllBrackets,
  add,
  remove,
};
